// Per-user settings in app_users.preferences (jsonb), e.g. report_favorites
// and tasks_view. One key is written at a time and MERGED into what is
// there: Reports used to write { report_favorites } as the whole object,
// which wiped every other key the person had saved (2026-10-21).
import { emailFilterValue } from "./helpers.js";

const row = (sb, companyId, email) => sb.from("app_users").select("preferences")
  .eq("company_id", companyId).ilike("email", emailFilterValue(email)).limit(1);

/** The person's preferences object ({} when none). { ok, prefs, error } */
export async function loadUserPrefs(sb, companyId, email) {
  if (!companyId || !email) return { ok: false, prefs: {}, error: { message: "no user" } };
  const { data, error } = await row(sb, companyId, email);
  if (error) return { ok: false, prefs: {}, error };
  const p = (data || [])[0]?.preferences;
  return { ok: true, prefs: p && typeof p === "object" && !Array.isArray(p) ? p : {}, error: null };
}

/** Read, set one key, write back the whole object. { ok, error } */
export async function saveUserPref(sb, companyId, email, key, value) {
  const cur = await loadUserPrefs(sb, companyId, email);
  if (!cur.ok) return { ok: false, error: cur.error };
  const { data, error } = await sb.from("app_users").update({ preferences: { ...cur.prefs, [key]: value } })
    .eq("company_id", companyId).ilike("email", emailFilterValue(email)).select("id");
  // No row for this person in this company (an admin added as a member has
  // none): nothing was saved, so say so rather than report success.
  if (!error && Array.isArray(data) && data.length === 0) return { ok: false, error: { message: "no settings row for this person" } };
  return { ok: !error, error };
}

// Settings that belong to the PERSON, whatever company they are in: kept on
// their sign-in account (auth user_metadata.prefs), which every user has.
// app_users rows exist only for some members -- Sigma's admin has none -- so
// a view choice saved there was silently lost (2026-10-21).
export async function loadAccountPref(sb, key) {
  const { data, error } = await sb.auth.getUser();
  if (error || !data?.user) return { ok: false, value: undefined, error: error || { message: "not signed in" } };
  const prefs = data.user.user_metadata?.prefs;
  return { ok: true, value: prefs && typeof prefs === "object" ? prefs[key] : undefined, error: null };
}

/** Read the account's prefs, set one key, write them back (other keys kept). */
export async function saveAccountPref(sb, key, value) {
  const { data, error } = await sb.auth.getUser();
  if (error || !data?.user) return { ok: false, error: error || { message: "not signed in" } };
  const cur = data.user.user_metadata?.prefs;
  const prefs = { ...(cur && typeof cur === "object" && !Array.isArray(cur) ? cur : {}), [key]: value };
  const { error: upErr } = await sb.auth.updateUser({ data: { prefs } });
  return { ok: !upErr, error: upErr || null };
}
